import { FC } from 'react'
import cn from './index.module.css'
import BasicTextField from '../../../BasicTextField/BasicTextField'
import SecondaryButton from '../../../SecondaryButton/SecondaryButton'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { Segmented } from 'antd'
import CheckboxLabel from '../../../CheckboxLabel/CheckboxLabel'
import classNames from 'classnames'
import { normalizeNumberInput } from '../../../BasicTextField/helper'

interface IMonthlyPaymentTable {
  tariff: ICourseTariff
  /** Без редактирования — только просмотр периодов/цен (CRM ссылка на покупку). */
  readOnly?: boolean
  updateTariff?: (
    field: keyof ICourseTariff,
    value: number | string | boolean | null | object,
  ) => void
}

const MonthlyPaymentTable: FC<IMonthlyPaymentTable> = ({
  tariff,
  updateTariff,
  readOnly = false,
}) => {
  const periods =
    tariff.type === 'stream_custom_period'
      ? tariff?.tariffPeriods ?? []
      : tariff.type === 'stream_custom_days'
      ? tariff?.tariffDays ?? []
      : tariff?.tariffWeeks ?? []

  const tariffPeriodsField =
    tariff.type === 'stream_custom_period'
      ? 'tariffPeriods'
      : tariff.type === 'stream_custom_days'
      ? 'tariffDays'
      : 'tariffWeeks'

  const tariffPeriodsFieldName =
    tariff.type === 'stream_custom_period'
      ? 'months'
      : tariff.type === 'stream_custom_days'
      ? 'days'
      : 'weeks'

  const periodsValues: { label: string; value: string }[] = [
    { label: 'Дней', value: 'stream_custom_days' },
    { label: 'Недель', value: 'stream_custom_weeks' },
    { label: 'Месяцев', value: 'stream_custom_period' },
  ]

  const addNewRule = () => {
    if (!updateTariff) return
    updateTariff(tariffPeriodsField, [
      ...periods,
      {
        [tariffPeriodsFieldName]: 0,
        price: 0,
        old_price: 0,
        name: '',
      },
    ])
  }

  const changePeriodUnits = (value: string) => updateTariff?.('type', value)

  const removeRule = (index: number) => {
    if (!updateTariff) return
    if (tariff.type === 'stream_custom_period') {
      updateTariff(
        'tariffPeriods',
        (periods as ITariffPeriod[])?.filter(
          (_: ITariffPeriod, i: number) => i !== index,
        ),
      )
    }
    if (tariff.type === 'stream_custom_days') {
      updateTariff(
        'tariffDays',
        (periods as ITariffPeriodDays[])?.filter(
          (_: ITariffPeriodDays, i: number) => i !== index,
        ),
      )
    }
    if (tariff.type === 'stream_custom_weeks') {
      updateTariff(
        'tariffWeeks',
        (periods as ITariffPeriodWeeks[])?.filter(
          (_: ITariffPeriodWeeks, i: number) => i !== index,
        ),
      )
    }
  }

  const onChangeName = (value: string, index: number) => {
    if (!updateTariff) return
    updateTariff(
      tariffPeriodsField,
      periods?.map((item, i) => {
        if (i === index) {
          return { ...item, name: value }
        } else {
          return item
        }
      }),
    )
  }

  const onChangeValue = (value: string, index: number) => {
    if (!updateTariff) return
    updateTariff(
      tariffPeriodsField,
      periods?.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            [tariffPeriodsFieldName]: normalizeNumberInput({
              value,
              allowDecimals: false,
            }),
          }
        } else {
          return item
        }
      }),
    )
  }

  const onPriceChange = (value: string, index: number) => {
    if (!updateTariff) return
    updateTariff(
      tariffPeriodsField,
      periods?.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            price: normalizeNumberInput({
              value,
            }),
          }
        } else {
          return item
        }
      }),
    )
  }

  const onOldPriceChange = (value: string, index: number) => {
    if (!updateTariff) return
    updateTariff(
      tariffPeriodsField,
      periods?.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            old_price: normalizeNumberInput({
              value,
            }),
          }
        } else {
          return item
        }
      }),
    )
  }

  const durationHeader =
    tariff.type === 'stream_custom_period'
      ? 'Длительность (мес.)'
      : tariff.type === 'stream_custom_days'
      ? 'Длительность (дн.)'
      : 'Длительность (нед.)'

  const getPeriodValue = (
    item: ITariffPeriod | ITariffPeriodDays | ITariffPeriodWeeks,
  ) =>
    tariff.type === 'stream_custom_period'
      ? (item as ITariffPeriod)?.months?.toString()
      : tariff.type === 'stream_custom_days'
      ? (item as ITariffPeriodDays)?.days?.toString()
      : (item as ITariffPeriodWeeks)?.weeks?.toString()

  return (
    <div className={cn.container}>
      <div className={cn.tableWrapper}>
        <div
          className={classNames(
            readOnly ? cn.readOnlyRow : cn.row,
            readOnly ? cn.readOnlyExtendedRow : cn.extendedRow,
          )}
        >
          <div className={cn.headerCell}>Название</div>
          <div className={cn.headerCell}>
            {readOnly ? durationHeader : 'Длительность доступа к курсу'}
          </div>
          <div className={cn.headerCell}>Стоимость</div>
          <div className={cn.headerCell}>Старая цена</div>
          {!readOnly && <div className={cn.headerCell}></div>}
        </div>
        {periods?.length === 0 && (
          <div className={cn.emptyTableStub}>
            {readOnly ? 'Нет периодов' : 'Добавьте правила'}
          </div>
        )}
        {periods?.map((item, index) => {
          const value = getPeriodValue(item)

          return (
            <div
              className={classNames(
                readOnly ? cn.readOnlyRow : cn.row,
                readOnly ? cn.readOnlyExtendedRow : cn.extendedRow,
              )}
              key={index}
            >
              {readOnly ? (
                <div className={cn.cell}>{item?.name?.toString() || '—'}</div>
              ) : (
                <BasicTextField
                  className={cn.textField}
                  value={item?.name?.toString()}
                  onChange={(value) => onChangeName(value, index)}
                />
              )}
              {readOnly ? (
                <div className={cn.cell}>{value || '—'}</div>
              ) : (
                <BasicTextField
                  className={cn.textField}
                  value={value}
                  onChange={(value) => onChangeValue(value, index)}
                />
              )}
              {readOnly ? (
                <div className={cn.cell}>
                  {item?.price != null ? `${item.price} ₽` : '—'}
                </div>
              ) : (
                <BasicTextField
                  className={cn.textField}
                  value={item?.price?.toString()}
                  onChange={(value) => onPriceChange(value, index)}
                />
              )}
              {readOnly ? (
                <div className={cn.cell}>
                  {item?.old_price != null ? `${item.old_price} ₽` : '—'}
                </div>
              ) : (
                <BasicTextField
                  className={cn.textField}
                  value={item?.old_price?.toString()}
                  onChange={(value) => onOldPriceChange(value, index)}
                />
              )}
              {!readOnly && (
                <div className={cn.buttonCell}>
                  <button
                    className={cn.removeRuleButton}
                    onClick={() => removeRule(index)}
                  >
                    <CloseOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {!readOnly && (
        <>
          <div className={cn.actions}>
            <Segmented
              disabled={tariff?.is_used}
              className={cn.segmented}
              value={tariff?.type}
              onChange={(value) => changePeriodUnits(value as string)}
              options={periodsValues}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
            />
            <SecondaryButton onClick={addNewRule} className={cn.addButton}>
              <PlusOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </SecondaryButton>
          </div>
          {tariff.type === 'stream_custom_weeks' && (
            <div className={cn.actions}>
              <CheckboxLabel
                label="При записи учеником на курс писать текст: Выберите количество занятий"
                value={!!tariff?.weekly_tariff_text_type}
                onChange={(value) =>
                  updateTariff?.('weekly_tariff_text_type', value ? 1 : 0)
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MonthlyPaymentTable
